import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { clearUser } from "../../features/user/userSlice";
import { toast } from "../../components/common/Toast";
import { getSocket, disconnectSocket } from "../../utils/socket";

const css = `
.ffh-head{position:sticky;top:0;z-index:99;background:rgba(255,255,255,.96);backdrop-filter:saturate(1.4) blur(8px);border-bottom:1px solid #D9E3EE;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif}
.ffh-head *{box-sizing:border-box}
.ffh-head .ffh-head-wrap{max-width:1160px;margin:0 auto;padding:0 20px;display:flex;align-items:center;gap:20px;min-height:68px}
.ffh-head .logo{flex:0 0 auto;display:flex;align-items:center;margin:0}
.ffh-head .logo a{display:inline-flex;align-items:center}
.ffh-head .logo img{display:block;max-height:48px;width:auto}
.ffh-head .ffh-nav{display:flex;gap:2px;margin-left:auto;flex-wrap:nowrap}
.ffh-head .ffh-nav a{color:#16263A;text-decoration:none;font-size:.9375rem;font-weight:600;padding:8px 9px;border-radius:8px;white-space:nowrap;line-height:1.4}
.ffh-head .ffh-nav a:hover{background:#EAF3FB}
.ffh-head .ffh-head-cta{display:flex;gap:14px;align-items:center;flex:0 0 auto}
.ffh-head .ffh-head-cta .signin_btn{white-space:nowrap}
.ffh-head .ffh-btn{white-space:nowrap;display:inline-flex;align-items:center;justify-content:center;min-height:40px;padding:0 16px;border-radius:999px;font-weight:700;font-size:.9375rem;text-decoration:none;border:2px solid transparent;background:#E8842C;color:#fff;transition:background .15s,transform .15s}
.ffh-head .ffh-btn:hover{background:#D27220;transform:translateY(-1px);color:#fff}
@media (max-width:1440px){.ffh-head .ffh-nav a:nth-child(4){display:none}}
@media (max-width:1100px){.ffh-head .ffh-head-wrap{flex-wrap:wrap;padding-top:8px;padding-bottom:8px;row-gap:4px}.ffh-head .ffh-head-cta{margin-left:auto}.ffh-head .ffh-nav{order:3;width:100%;margin-left:0;overflow-x:auto}.ffh-head .ffh-nav a:nth-child(4){display:inline-block}}
@media (max-width:560px){.ffh-head .ffh-btn{display:none}}
`;

// FIXED: this component used to run its own checkLoginStatus() on mount,
// hitting GET /api/auth/get-login directly — a route that doesn't exist
// anywhere in authRoutes.js (404, confirmed), fired twice per load under
// React 18 Strict Mode. There was never a reason for a second, independent
// fetch here: login already populates Redux's state.user.userInfo (and
// userSlice persists it to localStorage/sessionStorage and reloads it on
// boot — see loadInitialState() in userSlice.js), which is exactly what
// the dashboard's own Header.jsx already reads. Using that instead means
// this header renders immediately, with zero network calls, and can never
// drift out of sync with the rest of the app's auth state.
//
// Also removed: fetchPersonalityTestData() / get-personality-report call.
// Same situation as get-login — no matching route exists, and the
// "personality test" feature it's for was matrimony-era and is gone.
const Header = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.user.userInfo);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    const socket = getSocket();
    if (socket && socket.connected) {
      const userId = user?.id || user?.user_id;
      if (userId) {
        socket.emit("userOffline", { userId });
      }
    }
    disconnectSocket();

    // clearUser() already removes the token/userInfo from both
    // localStorage and sessionStorage (see userSlice.js) — no need to
    // touch localStorage directly here too.
    dispatch(clearUser());
    setIsDropdownOpen(false);

    navigate("/login");
    toast.success("Logged out successfully!");
  };

  return (
    <header className="ffh-head">
      <style>{css}</style>
      <div className="ffh-head-wrap">
        {/* LOGO (unchanged) */}
        <div className="logo">
          <Link className="navbar-brand" to="/">
            <img src="/images/logo.png" alt="Logo" />
          </Link>
        </div>

        <nav className="ffh-nav" aria-label="Main">
          <Link to="/how-it-works">How it works</Link>
          <Link to="/pregnancy-monitoring">Pregnancy</Link>
          <Link to="/surrogacy/wellness-monitoring">Surrogacy</Link>
          <Link to="/postpartum/long-term-health">Postpartum</Link>
          <Link to="/providers">For physicians</Link>
          <Link to="/partners">For clinics</Link>
          <Link to="/pricing">Pricing</Link>
        </nav>

        <div className="ffh-head-cta">
          <Link className="ffh-btn" to="/contact-us?type=member">
            Request Free Pilot Access
          </Link>
          {/* LOGIN / USER DROPDOWN (unchanged) */}
          {user ? (
            <div
              className="user-dropdown"
              ref={dropdownRef}
              style={{ position: "relative", marginLeft: "6px" }}
            >
              <button
                type="button"
                className="signin_btn loginbtn2"
                onClick={() => setIsDropdownOpen((v) => !v)}
                style={{
                  color: "#e8842c",
                  border: "1px solid #e8842c",
                  borderRadius: "24px",
                  background: "#fff",
                  padding: "8px 18px",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <i
                  className="fa fa-user-circle-o"
                  aria-hidden="true"
                  style={{ color: "#e8842c" }}
                />
                {user.first_name} {isDropdownOpen ? "▲" : "▼"}
              </button>

              {isDropdownOpen && (
                <div
                  className="dropdown-menu-custom"
                  style={{
                    position: "absolute",
                    right: 0,
                    left: "auto",
                    width: "180px",
                    marginTop: "10px",
                    borderRadius: "10px",
                    overflow: "hidden",
                    border: "1px solid #eee",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
                  }}
                >
                  <button
                    type="button"
                    style={{
                      border: "none",
                      outline: "none",
                      width: "100%",
                      textAlign: "left",
                      padding: "14px 20px",
                      background: "#fff",
                      cursor: "pointer",
                      transition: "background 0.15s ease",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#fdf3ea")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "#fff")
                    }
                    onClick={() => {
                      setIsDropdownOpen(false);
                      navigate("/dashboard", { state: { activtab: "dash" } });
                    }}
                  >
                    Dashboard
                  </button>

                  <button
                    type="button"
                    style={{
                      border: "none",
                      outline: "none",
                      width: "100%",
                      textAlign: "left",
                      padding: "14px 20px",
                      background: "#fff",
                      cursor: "pointer",
                      transition: "background 0.15s ease",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#fdf3ea")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "#fff")
                    }
                    onClick={() => {
                      setIsDropdownOpen(false);
                      navigate("/dashboard", {
                        state: { activtab: "settings" },
                      });
                    }}
                  >
                    Setting
                  </button>

                  <button
                    type="button"
                    style={{
                      border: "none",
                      outline: "none",
                      width: "100%",
                      textAlign: "left",
                      padding: "14px 20px",
                      background: "#fff",
                      cursor: "pointer",
                      transition: "background 0.15s ease",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = "#fdf3ea")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "#fff")
                    }
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" className="signin_btn loginbtn2">
              <i className="fa fa-user-circle-o" aria-hidden="true" />
              Login/Sign-Up
            </Link>
          )}
          
        </div>
      </div>
    </header>
  );
};

export default Header;